import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

import CustomButton from '../components/common/custom-button/custom-button.jsx';
import CustomTextInputField from '../components/common/custom-text-input-field/custom-text-input-field.jsx';
import Text from '../components/common/custom-text/custom-text.jsx';
import SubdomainStatusAdornment from './subdomain-status-adornment.jsx';

import { changePlan, saveWhiteLabelInfo } from '../api/signup-service.js';
import { subscriptionTiers } from '../constants/subscription-tiers.js';
import useSubdomainAvailability from '../hooks/useSubdomainAvailability.jsx';
import { useCurrentUser } from '../store/current-user-context.jsx';
import { slugify } from '../utils/string-utils.js';
import { isRequired, isValidSubdomain } from '../utils/validation-utils.js';

import '../styles/signup/signup.scss';

const SignupWhiteLabel = ({ formData = {}, dispatch, trackEventOnce }) => {
  const { agencyWhiteLabelSubdomain, companyName, domain, subdomain, plan } = formData;
  const [currentPlan, setCurrentPlan] = React.useState(plan);
  const [isUpgrading, setIsUpgrading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const onFreelancerPlan =
    currentPlan.value === subscriptionTiers.FREELANCER.value;
  const agencyOwnsSubdomain = agencyWhiteLabelSubdomain === subdomain;
  const shouldCheckSubdomain = !onFreelancerPlan && isValidSubdomain(subdomain) && !agencyOwnsSubdomain;
  const { available, loading, error } = useSubdomainAvailability(
    subdomain,
    shouldCheckSubdomain
  );
  const { user } = useCurrentUser();
  const { agencyId } = user || {};

  const handleNextStep = (whitelabel = false) => {
    trackEventOnce('registration_step2', {
      agency_id: agencyId,
      plan: currentPlan.name,
      whitelabel: whitelabel ? 'yes' : 'no',
    });

    dispatch({ type: 'UPDATE_ACTIVE_STEP', payload: 2 });
  };

  const isFormValid = () => {
    const validSubdomain = isValidSubdomain(subdomain);
    const subdomainIsAvailable = available || agencyOwnsSubdomain;
    return !!companyName && !!domain && validSubdomain && subdomainIsAvailable;
  };

  const upgradePlanToTeam = async () => {
    setIsUpgrading(true);
    try {
      const response = await changePlan({
        plan: subscriptionTiers.TEAM,
      });
      if (response) {
        dispatch({ type: 'UPDATE_PLAN', payload: subscriptionTiers.TEAM });
        setCurrentPlan(subscriptionTiers.TEAM);
      }
    } catch (error) {
      console.error('Error upgrading plan:', error);
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (isFormValid()) {
      setIsSubmitting(true);

      saveWhiteLabelInfo({
        agencyId,
        companyName,
        domain,
        subdomain,
      })
        .then(() => {
          dispatch({ type: 'UPDATE_AGENCY_WHITE_LABEL_SUBDOMAIN', payload: subdomain });
          handleNextStep(true);
        })
        .catch(error => {
          console.error('Error saving white label info:', error);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  React.useEffect(() => {
    if (companyName && !subdomain) {
      dispatch({ type: 'UPDATE_SUBDOMAIN', payload: slugify(companyName) });
    }
  }, [companyName]);

  return (
    <div className="register-container">
      <div className="register-form">
        <section className="signup-form">
          <form onSubmit={handleSubmit}>
            <Text as="h2" variant="l1" color="#2B4A78">
              Plan: {currentPlan.label}
            </Text>
            <header className="header-text">
              <Text as="h4" variant="h4" color="#2B4A78">
                White Label Setup
              </Text>
              <Text variant="b2" color="#2B4A78">
                White-labeling Cloud Campaign lets you customize the name, logo,
                colors, domain—and more—with just a few clicks. Check out this
                quick walkthrough of white-labeling in action!
              </Text>
            </header>
            {/* ARCADE EMBED START */}
            <div
              style={{
                position: 'relative',
                paddingBottom: 'calc(74.9144811858609% + 41px)',
                height: 0,
                width: '100%',
                marginBottom: '32px',
                marginTop: '16px',
              }}
            >
              <iframe
                src="https://demo.arcade.software/gxnyZCwDMRw6IHlMpi3E?embed&embed_mobile=inline&embed_desktop=inline&show_copy_link=true "
                title="White-Label Registration Walkthrough"
                frameBorder="0"
                loading="lazy"
                webkitallowfullscreen="true"
                mozallowfullscreen="true"
                allowFullScreen
                allow="clipboard-write"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  colorScheme: 'light',
                }}
              ></iframe>
            </div>
            {/* ARCADE EMBED END */}
            <CustomTextInputField
              label={'Company Name'}
              value={companyName}
              onChange={event =>
                dispatch({
                  type: 'UPDATE_COMPANY_NAME',
                  payload: event.target.value,
                })
              }
              required
              fullWidth
              disabled={onFreelancerPlan}
              validations={[
                {
                  validation: isRequired,
                  validationError: 'Enter a valid company name.',
                },
              ]}
              testid={'signup-wl-company-name'}
            />
            <fieldset className="domain-info">
              <legend>
                <Text variant="l2" color="#324975">
                  Domain
                </Text>
              </legend>
              <div className="domain-info-fields">
                <CustomTextInputField
                  label={'Subdomain'}
                  hideLabel={true}
                  value={subdomain}
                  onChange={event =>
                    dispatch({
                      type: 'UPDATE_SUBDOMAIN',
                      payload: event.target.value,
                    })
                  }
                  required
                  fullWidth
                  disabled={onFreelancerPlan}
                  validations={[
                    {
                      validation: isValidSubdomain,
                      validationError: 'Enter a valid subdomain.',
                    },
                  ]}
                  endAdornment={
                    shouldCheckSubdomain && (
                      <SubdomainStatusAdornment
                        loading={loading}
                        available={available}
                        error={error}
                      />
                    )
                  }
                  testid={'signup-wl-subdomain'}
                />
                <CustomTextInputField
                  label={'Domain'}
                  hideLabel={true}
                  value={'.' + domain}
                  styles={{ marginTop: '16px' }}
                  required
                  fullWidth
                  disabled
                  testid={'signup-wl-domain'}
                />
              </div>
            </fieldset>
            <div className="signup-next-step-container">
              <div />
              {onFreelancerPlan ? (
                <CustomButton
                  variant="primary"
                  faIcon={faChevronRight}
                  iconPosition="right"
                  onClick={() => handleNextStep(false)}
                  data-testid={'signup-wl-next'}
                >
                  Don't upgrade
                </CustomButton>
              ) : (
                <div className="wl-next-step-container">
                  <CustomButton
                    variant="tertiary"
                    onClick={() => handleNextStep(false)}
                    data-testid={'signup-wl-skip'}
                  >
                    Opt out
                  </CustomButton>
                  <CustomButton
                    type="submit"
                    variant="primary"
                    faIcon={faChevronRight}
                    iconPosition="right"
                    disabled={!isFormValid() || loading}
                    data-testid={'signup-wl-next'}
                    isLoading={isSubmitting}
                  >
                    Next Step
                  </CustomButton>
                </div>
              )}
            </div>
          </form>
        </section>
        {onFreelancerPlan && (
          <div className="testimonial-banner-container">
            <Text as="h4" variant="h3" color="#FFF">
              Unlock White-Labeling with the Team Plan
            </Text>
            <ul className="testimonial-list">
              <Text as="li" variant="bb1" color="#FFF">
                Personalize your dashboard with your logo, colors, and custom
                domain
              </Text>
              <Text as="li" variant="bb1" color="#FFF">
                Deliver a professional, branded experience for clients
              </Text>
              <Text as="li" variant="bb1" color="#FFF">
                Set yourself apart from competitors with a fully customized
                platform
              </Text>
            </ul>
            <CustomButton
              variant="secondary"
              fullWidth
              onClick={upgradePlanToTeam}
              style={{ border: 'none' }}
              isLoading={isUpgrading}
            >
              Upgrade to the Team Plan
            </CustomButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupWhiteLabel;
